/**
 *  @class
 *  @function CartGiftWrap
 *  Gift wrap in the cart drawer and on the cart page (cart-gift-wrap.liquid),
 *  through theme.cart (app.js).
 *  - A tick adds the wrap, then redraws the cart (cart:item-added).
 *  - An untick sets every wrap line to 0 and clears the gift message in one
 *    /cart/update.js, then redraws the cart (line-item:change:end).
 *  - When either fails, the tick box goes back and the error shows under it.
 *    When the server answered, the cart may not be what the page shows (the
 *    wrap was removed elsewhere, eg. in another tab): the cart is redrawn
 *    (theme.cart.refresh), and the redrawn block shows the error.
 *  - The message is saved 600ms after typing stops, as the "Gift message"
 *    cart attribute, with no redraw; at once when the field loses focus or
 *    the page is left (keepalive), so the last words aren't lost to a link
 *    or a wallet button. What was typed is kept here, so every redraw (which
 *    brings the server's copy, and "Message saved" once it is) and the other
 *    copy (drawer or cart page) show it.
 *  - A cart holding only the wrap loses it: once per page load, and once
 *    more after each tick, so a removal that fails can't repeat itself.
 *  - A message left on a cart without the wrap (its line was removed, or
 *    every line) is cleared.
 *  - The drawer's message row stays as the shopper opened or closed it.
 *  Each redraw brings new elements, so what they share lives in `shared`.
 */
if (!customElements.get('cart-gift-wrap')) {
  const MESSAGE = 'Gift message';
  const SAVE_DELAY = 600;
  const shared = {
    // A tick, an untick or the self-heal is on its way.
    busy: false,
    // The message as typed on this page; null: the server's copy is current.
    message: null,
    // The message typed has not been saved yet; it has been saved.
    unsaved: false,
    saved: false,
    // An error for the redrawn block of a surface: { surface, message,
    // until, checked: what the shopper asked for }.
    notice: null,
    // The drawer's message row as the shopper left it; null: as rendered.
    open: null,
    // The pending save, and the save on its way.
    timer: 0,
    saving: null,
    healed: false,
    clearing: false
  };

  const copies = () => Array.from(document.querySelectorAll('cart-gift-wrap'));

  const cancelSave = () => {
    clearTimeout(shared.timer);
    shared.timer = 0;
  };

  // keepalive: the save outlives the page (a link or a wallet button).
  const save = () => {
    shared.timer = 0;
    const value = shared.message;
    if (value === null) return;
    const request = theme.cart.update({ attributes: { [MESSAGE]: value } }, { sections: false, keepalive: true });
    shared.saving = request;
    request
      .then(() => {
        if (shared.message !== value) return;
        shared.unsaved = false;
        shared.saved = true;
        copies().forEach((copy) => copy.showSaved(true));
      }, (error) => {
        console.error(error);
        const list = copies();
        (list.find((copy) => copy.contains(document.activeElement)) || list[0])?.showError(describe(error));
      })
      .finally(() => {
        if (shared.saving === request) shared.saving = null;
      });
  };

  // A save still waiting for its delay goes now.
  const flush = () => {
    if (!shared.timer) return;
    cancelSave();
    save();
  };
  window.addEventListener('pagehide', flush);

  // The shopper's text: the Ajax API's description, else the cart's generic
  // error.
  const describe = (error) => (error && error.body && error.body.description) || theme.strings.cartError;

  class CartGiftWrap extends HTMLElement {
    connectedCallback() {
      if (!window.theme || !theme.cart) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.checkbox = this.querySelector('.cart-gift-wrap__checkbox');
      this.textarea = this.querySelector('.cart-gift-wrap__textarea');
      this.context = this.dataset.surface === 'cart' ? 'cart' : 'dialog';

      if (this.checkbox) {
        // One change at a time: a click while one is on its way does nothing.
        this.checkbox.addEventListener('click', (event) => {
          if (shared.busy) event.preventDefault();
        }, { signal });
        this.checkbox.addEventListener('change', () => this.toggle(), { signal });
      }
      const row = this.querySelector('.cart-gift-wrap__message details');
      if (row) {
        if (shared.open !== null) row.open = shared.open;
        row.addEventListener('toggle', () => {
          shared.open = row.open;
        }, { signal });
        // Writing in it keeps it open, even once the message is cleared.
        row.addEventListener('focusin', () => {
          shared.open = true;
        }, { signal });
      }
      if (this.textarea) {
        if (shared.message !== null && this.textarea.value !== shared.message) this.textarea.value = shared.message;
        if (shared.saved && shared.message !== null) this.showSaved(true);
        this.textarea.addEventListener('input', () => this.type(), { signal });
        this.textarea.addEventListener('change', flush, { signal });
      }
      // The error of a failed change that redrew the cart, unless the cart
      // is now what the shopper asked for (eg. the wrap had already gone).
      const notice = shared.notice;
      if (notice && notice.surface === this.dataset.surface) {
        shared.notice = null;
        if (Date.now() < notice.until && this.checkbox && this.checkbox.checked !== notice.checked) this.showError(notice.message);
      }

      if (this.hasAttribute('data-heal')) {
        this.heal();
      } else if (this.hasAttribute('data-clear-message')) {
        this.clearMessage();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    // The wrap lines' keys, or the wrap's variant id when none is known.
    lines() {
      let keys = [];
      try {
        keys = JSON.parse(this.dataset.keys || '[]');
      } catch (error) {
        console.error(error);
      }
      return keys.length ? keys : [this.dataset.variantId];
    }

    toggle() {
      const checked = this.checkbox.checked;
      shared.busy = true;
      // The shopper's own tick allows one more self-heal.
      if (checked) shared.healed = false;
      this.setBusy(true);
      this.hideError();
      const done = () => {
        shared.busy = false;
        this.setBusy(false);
      };
      (checked ? this.add() : this.remove()).then(done, (error) => {
        console.error(error);
        done();
        this.checkbox.checked = !checked;
        // An untick's refusal (Shopify's "updates parameter is invalid":
        // the wrap line has gone) means nothing to the shopper.
        const message = checked ? describe(error) : theme.strings.cartError;
        if (!(error && error.status)) {
          this.showError(message);
          return;
        }
        // The server answered: the redrawn block says whether it failed
        // (connectedCallback); this one does if no redraw comes.
        const notice = { surface: this.dataset.surface, message: message, until: Date.now() + 5000, checked: checked };
        shared.notice = notice;
        theme.cart.refresh();
        setTimeout(() => {
          if (shared.notice !== notice) return;
          shared.notice = null;
          if (this.isConnected) this.showError(message);
        }, 5000);
      });
    }

    // A wrap that was not in the cart had no message: one typed before it
    // was taken out (or every line was) is not shown again.
    add() {
      const requested = [{ id: parseInt(this.dataset.variantId, 10), quantity: 1 }];
      return theme.cart.add(requested).then((state) => {
        cancelSave();
        shared.message = null;
        shared.unsaved = false;
        shared.saved = false;
        this.redraw(state);
        theme.cart.added(requested, state, this.context);
      });
    }

    // Waits for a save on its way, so the message it carries can't come
    // back after it is cleared. A save the untick cancelled goes ahead when
    // the untick fails.
    remove() {
      const keys = this.lines();
      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });
      cancelSave();
      const pending = shared.saving ? shared.saving.catch(() => {}) : Promise.resolve();
      return pending
        .then(() => theme.cart.update({ updates: updates, attributes: { [MESSAGE]: '' } }))
        .then((cart) => {
          shared.message = null;
          shared.unsaved = false;
          shared.saved = false;
          this.redraw(cart);
          theme.cart.changed(keys.map((key) => ({ key: key, quantity: 0 })), cart, this.context);
        }, (error) => {
          if (shared.unsaved && !shared.timer) shared.timer = setTimeout(save, SAVE_DELAY);
          throw error;
        });
    }

    // An accepted change: an error while redrawing it is logged, nothing more.
    redraw(state) {
      try {
        theme.cart.render(state);
      } catch (error) {
        console.error(error);
      }
    }

    heal() {
      if (shared.healed || shared.busy) return;
      shared.healed = true;
      shared.busy = true;
      this.remove()
        .catch((error) => console.error(error))
        .finally(() => {
          shared.busy = false;
        });
    }

    clearMessage() {
      if (shared.clearing || shared.busy) return;
      shared.clearing = true;
      cancelSave();
      shared.message = null;
      shared.unsaved = false;
      shared.saved = false;
      theme.cart.update({ attributes: { [MESSAGE]: '' } }, { sections: false })
        .catch((error) => console.error(error))
        .finally(() => {
          shared.clearing = false;
        });
    }

    type() {
      shared.message = this.textarea.value;
      shared.unsaved = true;
      shared.saved = false;
      this.hideError();
      copies().forEach((copy) => {
        copy.showSaved(false);
        if (copy !== this && copy.textarea && copy.textarea.value !== shared.message) copy.textarea.value = shared.message;
      });
      cancelSave();
      shared.timer = setTimeout(save, SAVE_DELAY);
    }

    setBusy(busy) {
      this.classList.toggle('cart-gift-wrap--loading', busy);
    }

    showSaved(saved) {
      this.querySelector('.cart-gift-wrap__saved')?.toggleAttribute('hidden', !saved);
    }

    showError(message) {
      const notice = this.querySelector('.cart-gift-wrap__error');
      if (!notice) return;
      notice.querySelector('.cart-item__error-text').textContent = message;
      notice.removeAttribute('hidden');
    }

    hideError() {
      shared.notice = null;
      this.querySelector('.cart-gift-wrap__error')?.setAttribute('hidden', '');
    }
  }
  customElements.define('cart-gift-wrap', CartGiftWrap);
}
